// 메모리 예산 원장 — 「무엇이 얼마를 먹나」를 재는 **단 하나의 자리**
//
// ── 왜 신설했나 ────────────────────────────────────────────────────────────────
// 2026-08-17~18 에 `RangeError: Array buffer allocation failed` 가 실유저에게서
// **세 지점**에서 났다(FarTerrain.jsx:181 · hfBuild.js:590 · lodVoxelize.js:140).
// 한 곳을 try/catch 로 막으면 다음으로 큰 할당이 대신 죽었다 — 08-17 22:11 에
// 원경 지형을 고쳤더니 00:19 에 근경 지형이, 00:39 에 임포스터가 터졌다.
//
// ★ 스택은 **「어디서 죽었나」만** 말한다. 「그 순간 무엇이 얼마를 먹고 있었나」는
//   한 줄도 안 말한다. 그래서 매번 진범을 못 짚고 **죽은 자리만** 고쳤다.
//   이 파일은 그 빠진 축 하나를 위해 있다 — 죽은 자리가 아니라 **점유표**를 남긴다.
//
// ── 이 원장이 정직하게 말하는 것과 못 말하는 것 ────────────────────────────────
//  ○ 말한다: 우리가 이 문을 통해 잡은 타입드 배열의 태그별 누적·현재·최대
//  ○ 말한다: 할당이 실패한 그 순간의 점유 상위와 JS heap 잔량
//  ✗ 못 말한다: **놓아준 것**을 전부는 못 본다. `release()` 를 부르는 곳만 반영된다.
//              지오메트리가 dispose 될 때 우리 원장은 모르는 경우가 있다.
//  ✗ 못 말한다: GPU 쪽(VRAM). 텍스처·업로드된 버퍼는 여기 안 잡힌다.
//  → 그래서 **상주 조사는 `memCensus.js` 가 따로 한다**(씬을 실제로 훑는다).
//    둘은 경쟁이 아니라 서로의 사각지대다: 여기는 「잡는 순간」, 거기는 「지금 떠 있는 것」.
//
// ── 제약: 이 파일은 **워커에서도 돈다** ────────────────────────────────────────
// hfBuild.js 가 hfWorker.js 안에서 실행되므로 `window`·`document`·THREE·정적 import
// 를 쓰면 워커가 통째로 깨진다. 그래서 **zero-import 순수 모듈**이고, 바깥 세계는
// 전부 `typeof` 가드 + 동적 import 로만 만진다. hfConstants.js 와 같은 규약.

const B = { Float32Array: 4, Float64Array: 8, Uint32Array: 4, Int32Array: 4,
            Uint16Array: 2, Int16Array: 2, Uint8Array: 1, Int8Array: 1 };

/** tag -> { cur, peak, sum, n } — cur 는 release 를 부른 만큼만 정확하다(위 주석 참조). */
const L = new Map();
let curTotal = 0, peakTotal = 0;
const FAILS = [];            // 최근 실패 8건 — 세션 안에서 되짚어 볼 수 있게 남긴다
let failSent = 0;            // 서버로 보낸 실패 수 (같은 실패로 로그를 도배하지 않는다)

// 실패 훅 — 메인 스레드에서 memCensus 가 등록한다(워커에선 아무도 안 건다).
// ★ 왜 훅인가: 이 파일은 zero-import 규약이라 memCensus 를 직접 못 부른다(워커가 깨진다).
//   그런데 실패 순간의 **상주 점유표**야말로 우리가 여태 한 번도 못 봤던 것이다 —
//   iOS 는 `performance.memory` 를 아예 안 줘서 heap 감시가 안 돌기 때문에,
//   「실패했을 때 붙는다」가 유일하게 모든 기기에서 작동하는 경로다.
let failHook = null;
export function onFail(fn) { failHook = fn; }

const ent = (tag) => {
  let e = L.get(tag);
  if (!e) { e = { cur: 0, peak: 0, sum: 0, n: 0 }; L.set(tag, e); }
  return e;
};

const mb = (b) => (b / 1048576);

/** 지금 이 스레드의 JS heap. Chrome 계열만 준다 — 없으면 null 이지 0 이 아니다. */
export function heap() {
  const m = (typeof performance !== 'undefined') && performance.memory;
  if (!m) return null;
  return { used: m.usedJSHeapSize, total: m.totalJSHeapSize, limit: m.jsHeapSizeLimit };
}

/** 점유 상위 n개 — 실패 진단의 본문이다. */
export function top(n = 6) {
  return [...L.entries()]
    .map(([tag, e]) => ({ tag, cur: e.cur, peak: e.peak, sum: e.sum, n: e.n }))
    .sort((a, b) => b.cur - a.cur)
    .slice(0, n);
}

export function ledger() {
  return { curTotal, peakTotal, heap: heap(), top: top(12), fails: FAILS.slice(-4) };
}

/** 로그 한 줄 — 서버 jsonl 에 이 형식으로 쌓인다. */
export function line() {
  const h = heap();
  const hs = h ? ` | heap ${mb(h.used).toFixed(0)}/${mb(h.limit).toFixed(0)}MB` : '';
  const t = top(6).map((x) => `${x.tag} ${mb(x.cur).toFixed(1)}`).join(' · ');
  return `[MEM] 현재 ${mb(curTotal).toFixed(1)}MB · 최대 ${mb(peakTotal).toFixed(1)}MB${hs} | ${t}`;
}

/**
 * 실패를 원장에 적고 서버로 한 번 보낸다.
 * ★ 여기가 이 파일의 존재 이유다 — RangeError 하나에 **그 순간의 점유표**를 붙여 보낸다.
 */
function recordFail(tag, want, err) {
  const snap = { tag, wantMB: +mb(want).toFixed(2), curMB: +mb(curTotal).toFixed(1),
                 peakMB: +mb(peakTotal).toFixed(1),
                 top: top(6).map((x) => `${x.tag}=${mb(x.cur).toFixed(1)}MB`),
                 heap: heap() ? `${mb(heap().used).toFixed(0)}/${mb(heap().limit).toFixed(0)}MB` : 'n/a',
                 err: String(err && err.message || err) };
  FAILS.push(snap);
  if (FAILS.length > 8) FAILS.shift();
  // 훅이 붙어 있으면 상주 조사를 같이 낸다. 훅이 던져도 진단이 죽으면 안 된다.
  try { failHook?.(snap); } catch (_) {}

  // 한 세션에서 1건까지만 보낸다 — 메모리가 마르면 같은 실패가
  // 초당 여러 번 나서 원래는 2건이었는데, 실측으로 이 훅이
  // **0번** 발동했다 — 안드로이드 강제종료(OOM kill)는 JS 가 RangeError 를 던질 기회
  // 자체가 없다(handoff mobile-oom-diagnosis §2). 그 예산을 상주조사(census, WARN+CRIT
  // 2회)로 옮겼다 — 죽기 직전까지 관측하는 쪽이 실측상 훨씬 잘 쓰인다.
  if (failSent >= 1 || typeof window === 'undefined') return;
  failSent++;
  const msg = `[MEM] 할당 실패 tag=${snap.tag} 요청=${snap.wantMB}MB `
            + `| 원장 현재 ${snap.curMB}MB 최대 ${snap.peakMB}MB | heap ${snap.heap} `
            + `| 상위: ${snap.top.join(' · ')}`;
  // ★ 여기서 `import('./clientLogReporter.js')` 를 하면 **워커가 통째로 뚱뚱해진다**
  //. Vite 는 워커를 단일 파일로 굽기 때문에 동적 import 도 인라인된다 —
  //   이 파일은 `hfBuild.js` 를 거쳐 `hfWorker.js` 안에서 도는데, 로거가 물고 있는
  //   `@react-three/drei` → three 까지 전부 따라 들어와 **hfWorker 가 564KB** 였다
  //   (형제 워커는 1.8KB · 5.7KB). 실제 일하는 코드는 9.7KB 뿐이다.
  //   `typeof window` 가드로는 못 막는다 — 번들러는 런타임 조건을 안 읽는다.
  //   그래서 **주입**으로 바꾼다: 메인 쪽이 부팅 때 한 번 꽂고, 워커는 안 꽂는다.
  if (_report) { try { _report('membudget', msg); } catch { /* 로그 실패로 죽지 않는다 */ } }
}

/** 리포터 주입 구멍 — 메인 스레드가 부팅 때 한 번 꽂는다(`main.jsx`). 워커는 안 꽂으므로
 *  아무것도 딸려오지 않고, 안 꽂힌 동안 메모리 경고는 조용히 버려진다(워커엔 보낼 곳이 없다). */
let _report = null;
export const setMemReporter = (fn) => { _report = fn; };

/**
 * 타입드 배열을 **이 문을 통해서** 잡는다.
 *
 * 실패해도 삼키지 않고 그대로 throw 한다 — 물러날지 포기할지는 호출부가 정한다
 * (`withFallback` 이 그 결정을 한 곳에 모아 둔다). 여기서 조용히 null 을 주면
 * 「메모리가 없어서 안 그려진 것」과 「그릴 게 없어서 안 그려진 것」이 안 갈린다.
 */
export function alloc(tag, Ctor, len) {
  const want = (B[Ctor.name] || 4) * len;
  let a;
  try {
    a = new Ctor(len);
  } catch (err) {
    recordFail(tag, want, err);
    throw err;
  }
  const e = ent(tag);
  e.cur += want; e.sum += want; e.n++;
  if (e.cur > e.peak) e.peak = e.cur;
  curTotal += want;
  if (curTotal > peakTotal) peakTotal = curTotal;
  return a;
}

/** 놓아줬다고 적는다. arr 또는 바이트 수. **부르는 곳만 정확해진다.** */
export function release(tag, arrOrBytes) {
  const bytes = typeof arrOrBytes === 'number' ? arrOrBytes : (arrOrBytes?.byteLength || 0);
  if (!bytes) return;
  const e = ent(tag);
  e.cur = Math.max(0, e.cur - bytes);
  curTotal = Math.max(0, curTotal - bytes);
}

/**
 * 우리가 안 잡았지만 **덩치를 아는 것**을 원장에 얹는다(GLB 원본 바이트 등).
 * 점유표에서 「지형만 크게 보이고 도시 GLB 는 안 보이는」 착시를 막는다.
 */
export function note(tag, bytes) {
  if (!bytes) return;
  const e = ent(tag);
  e.cur += bytes; e.sum += bytes; e.n++;
  if (e.cur > e.peak) e.peak = e.cur;
  curTotal += bytes;
  if (curTotal > peakTotal) peakTotal = curTotal;
}

/**
 * `note()` 와 같은 자리지만 **덮어쓴다** — 「현재 총량」을 주기적으로 다시 알려 오는 축용.
 *
 * ⚠ 왜 갈랐나 (2026-08-19): `note()` 는 `cur += bytes` 라 **같은 태그로 다시 부르면 더해진다.**
 *   `hf.tiles:<key>` 는 타일이 들고 날 때마다 **그 시점 총량**을 다시 note 하고 있었고,
 *   그래서 실측 0.6MB 가 원장에는 **14.9MB(25배)** 로, 명관님 PERF HUD 에는 **130.3MB** 로
 *   찍혔다. 「상한 96장이 안 먹는다」는 잘못된 진단이 여기서 나왔다 — 상한은 멀쩡했고
 *   **계기판이 거짓말**을 하고 있었다. 누적이 필요한 축(`note`)과 현재값을 보고하는 축
 *   (`noteSet`)은 성질이 다르므로 함수를 나눈다.
 */
export function noteSet(tag, bytes) {
  const e = ent(tag);
  const b = bytes || 0;
  if (e.cur === b) return;
  curTotal = Math.max(0, curTotal - e.cur + b);
  e.cur = b;
  if (e.cur > e.peak) e.peak = e.cur;
  if (curTotal > peakTotal) peakTotal = curTotal;
}

/**
 * 못 잡으면 **한 단계 물러나 다시 시도**한다 — 물러남을 이 파일 하나로 모은다.
 *
 * FarTerrain 이 08-17 에 자기 자리에서 이걸 손으로 짰고, 그때 배운 것이 있다:
 * **기기별 상한을 숫자로 못 적는다.** 같은 설정이 어떤 탭에선 되고 어떤 탭에선
 * 안 된다(그 시점에 다른 게 얼마나 먹고 있느냐의 문제다). 그러니 「이 기기는 몇 MB」
 * 를 표로 만들지 말고 **잡아 보고 실패하면 내려간다**.
 *
 * @param steps 시도할 인자 목록 — 앞이 가장 고운 것. 전부 실패하면 null.
 */
export function withFallback(tag, steps, make) {
  for (let i = 0; i < steps.length; i++) {
    try {
      const r = make(steps[i], i);
      if (r != null) {
        if (i > 0 && typeof console !== 'undefined') {
          console.warn(`[MEM] ${tag}: ${JSON.stringify(steps[0])} → ${JSON.stringify(steps[i])} `
                       + '로 물러났다 — 그 크기로는 버퍼를 못 잡는다');
        }
        return r;
      }
    } catch (err) {
      if (!/allocation failed|Invalid (typed )?array length|Array buffer/i.test(String(err?.message))) throw err;
      // 메모리 사유일 때만 물러난다. 다른 예외를 여기서 삼키면 진짜 버그가 「메모리 부족」으로 위장된다.
    }
  }
  return null;
}

/** 세션 안에서 다시 재고 싶을 때(콘솔용). */
export function reset() {
  L.clear(); curTotal = 0; peakTotal = 0; FAILS.length = 0; failSent = 0;
}

// 콘솔 손잡이 — 워커에는 window 가 없으므로 가드한다.
if (typeof window !== 'undefined') {
  window.__mem = { ledger, line, top, heap, reset, alloc, note, noteSet };
}
